import { z } from 'zod';

/**
 * How serious a violation type is held to be.
 *
 * **Separate from the action, and deliberately so.** The action is what the
 * platform *does*; the severity is what staff *think*, and the two are set
 * independently because an operator may well decide that a first offence of a
 * grave kind still earns only a warning while it beds in. Folding them into one
 * field would make every retuning of the response also a rewriting of the
 * record — the same argument `report_status` and `report_resolution` make.
 *
 * It is what the review queue sorts and filters on, and what a reviewer reads
 * first when a member's history has several entries in it.
 */
export const VIOLATION_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

export const ViolationSeveritySchema = z.enum(VIOLATION_SEVERITIES);

export type ViolationSeverity = (typeof VIOLATION_SEVERITIES)[number];

/**
 * The sortable rank — higher is graver.
 *
 * A function over the declared order rather than a stored number, so adding a
 * severity cannot leave two of them sharing a rank. The queue orders by this
 * descending, then by age ascending, so the gravest untreated case is the one a
 * reviewer opens.
 */
export function violationSeverityRank(severity: ViolationSeverity): number {
  return VIOLATION_SEVERITIES.indexOf(severity);
}
